import { useMemo } from 'react';

import { useQuery } from 'react-query';

import { mapFicFidcCockpitConcentration } from './mapFicFidcCockpitConcentration';
import { getFicFidcCockpitConcentration } from './service';

import type {
  FicFidcCockpitConcentrationData,
  FicFidcCockpitConcentrationStatus,
} from './mapFicFidcCockpitConcentration';

export type FicFidcCockpitConcentrationResultStatus =
  | FicFidcCockpitConcentrationStatus
  | 'loading'
  | 'error';

export interface FicFidcCockpitConcentrationResult {
  status: FicFidcCockpitConcentrationResultStatus;
  /** Dados mapeados do contrato v1; null durante loading, erro e consulta desabilitada. */
  data: FicFidcCockpitConcentrationData | null;
  competencyDate: string;
}

/**
 * View-model compartilhado dos widgets de concentração (cards Top 1/5/10/25
 * e gráfico resumido por fundo). Uma única consulta por
 * `operationId + competência`, mapeada pelo mesmo adaptador
 * (`mapFicFidcCockpitConcentration`), que valida o eco da competência
 * autoritativa sem fallback silencioso. Consumir o hook em ambos os
 * widgets reutiliza o mesmo cache do react-query.
 */
export function useFicFidcCockpitConcentration(
  operationId: string,
  competencyDate: string,
): FicFidcCockpitConcentrationResult {
  const query = useQuery<
    Awaited<ReturnType<typeof getFicFidcCockpitConcentration>>,
    Error
  >({
    queryKey: ['fic-fidc-cockpit-concentration', operationId, competencyDate],
    queryFn: () => getFicFidcCockpitConcentration(operationId, competencyDate),
    enabled: Boolean(operationId) && Boolean(competencyDate),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const data = useMemo(
    () =>
      query.data
        ? mapFicFidcCockpitConcentration(query.data, competencyDate)
        : null,
    [query.data, competencyDate],
  );

  const status: FicFidcCockpitConcentrationResultStatus = query.isError
    ? 'error'
    : query.isLoading
      ? 'loading'
      : data?.status ?? 'loading';

  return { status, data, competencyDate };
}
